/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, type ReactNode } from "react";
import { type MatrixClient, type Room } from "matrix-js-sdk";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@vector-im/compound-web";

import styles from "./KnockRequestsPanel.module.css";
import { Avatar, Size } from "../Avatar";
import { useKnockRequests } from "./useKnockRequests";

interface Props {
  client: MatrixClient;
  room: Room;
}

/**
 * Lists the people asking to join the call and lets whoever may invite people
 * to the room answer them. Renders nothing for everyone else, and while nobody
 * is knocking.
 */
export const KnockRequestsPanel: FC<Props> = ({ client, room }): ReactNode => {
  const { t } = useTranslation();
  const { requests, admit, decline } = useKnockRequests(client, room);

  if (requests.length === 0 || admit === undefined) return null;

  return (
    <section className={styles.panel} aria-label={t("knock_requests.title")}>
      <ul className={styles.list}>
        {requests.map(({ userId, name, avatarMxcUrl }) => (
          <li key={userId} className={styles.request}>
            <Avatar
              id={userId}
              name={name}
              src={avatarMxcUrl ?? undefined}
              size={Size.SM}
            />
            <Text className={styles.name} size="sm" weight="semibold">
              {t("knock_requests.wants_to_join", { name })}
            </Text>
            <div className={styles.actions}>
              <Button
                size="md"
                kind="primary"
                onClick={(): void => admit(userId)}
                aria-label={t("knock_requests.admit_label", { name })}
              >
                {t("knock_requests.admit")}
              </Button>
              {decline && (
                <Button
                  size="md"
                  kind="secondary"
                  destructive
                  onClick={(): void => decline(userId)}
                  aria-label={t("knock_requests.decline_label", { name })}
                >
                  {t("knock_requests.decline")}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
